import { useMemo } from 'react';

import { useGetScanDataSftpCredentialsWithStoreDetailsListQuery } from '@occc/graphql';
import { usePaginationParams } from '@occc-app/hooks/usePaginationParams';
import { usePaginatedQuery } from '@occc-app/hooks/usePaginatedQuery';

import { GenericSubmissionGroup } from '../scan-data-submission-groups.types';
import {
  ALTRIA_AFTER_PARAM,
  ALTRIA_BEFORE_PARAM,
  DEFAULT_PAGE_INFO_PATH,
  DEFAULT_ALTRIA_QUERY_VARIABLES,
  DEFAULT_CIRCANA_QUERY_VARIABLES,
  DEFAULT_MSA_QUERY_VARIABLES,
  MSA_AFTER_PARAM,
  MSA_BEFORE_PARAM,
  RJR_AFTER_PARAM,
  RJR_BEFORE_PARAM,
} from '../scan-data-submission-groups.constants';
import { mapSubmissionGroups } from '../scan-data-submission-groups.utils';
import { LoadedDataWithPagination } from '../../../scan-data.types';

type UseScanDataSubmissionGroupsParams = {
  pageSizeAltria?: number;
  pageSizeMsa?: number;
  pageSizeRjr?: number;
};

export const useScanDataSubmissionGroups = ({
  pageSizeAltria,
  pageSizeMsa,
  pageSizeRjr,
}: UseScanDataSubmissionGroupsParams = {}) => {
  const [altriaPaginationInfo, onAltriaPageChange] = usePaginationParams({
    afterParamName: ALTRIA_AFTER_PARAM,
    beforeParamName: ALTRIA_BEFORE_PARAM,
    pageSize: pageSizeAltria,
  });

  const [msaPaginationInfo, onMsaPageChange] = usePaginationParams({
    afterParamName: MSA_AFTER_PARAM,
    beforeParamName: MSA_BEFORE_PARAM,
    pageSize: pageSizeMsa,
  });

  const [rjrPaginationInfo, onRjrPageChange] = usePaginationParams({
    afterParamName: RJR_AFTER_PARAM,
    beforeParamName: RJR_BEFORE_PARAM,
    pageSize: pageSizeRjr,
  });

  const {
    data: altriaData,
    loading: altriaLoading,
    error: altriaError,
    paginationProps: altriaPaginationProps,
  } = usePaginatedQuery(
    useGetScanDataSftpCredentialsWithStoreDetailsListQuery,
    DEFAULT_PAGE_INFO_PATH,
    pageSizeAltria,
    DEFAULT_ALTRIA_QUERY_VARIABLES,
    altriaPaginationInfo,
    onAltriaPageChange,
  );

  const {
    data: msaData,
    loading: msaLoading,
    error: msaError,
    paginationProps: msaPaginationProps,
  } = usePaginatedQuery(
    useGetScanDataSftpCredentialsWithStoreDetailsListQuery,
    DEFAULT_PAGE_INFO_PATH,
    pageSizeMsa,
    DEFAULT_MSA_QUERY_VARIABLES,
    msaPaginationInfo,
    onMsaPageChange,
  );

  const {
    data: rjrData,
    loading: rjrLoading,
    error: rjrError,
    paginationProps: rjrPaginationProps,
  } = usePaginatedQuery(
    useGetScanDataSftpCredentialsWithStoreDetailsListQuery,
    DEFAULT_PAGE_INFO_PATH,
    pageSizeRjr,
    DEFAULT_CIRCANA_QUERY_VARIABLES,
    rjrPaginationInfo,
    onRjrPageChange,
  );

  const altriaGroups = useMemo(() => mapSubmissionGroups(altriaData), [altriaData]);

  const msaGroups = useMemo(() => mapSubmissionGroups(msaData), [msaData]);

  const rjrGroups = useMemo(() => mapSubmissionGroups(rjrData), [rjrData]);

  const altriaLoadedDataWithPagination: LoadedDataWithPagination<GenericSubmissionGroup[]> = {
    paginationProps: altriaPaginationProps,
    data: altriaGroups,
    loading: altriaLoading,
    error: altriaError,
  };
  const msaLoadedDataWithPagination: LoadedDataWithPagination<GenericSubmissionGroup[]> = {
    paginationProps: msaPaginationProps,
    data: msaGroups,
    loading: msaLoading,
    error: msaError,
  };
  const rjrLoadedDataWithPagination: LoadedDataWithPagination<GenericSubmissionGroup[]> = {
    paginationProps: rjrPaginationProps,
    data: rjrGroups,
    loading: rjrLoading,
    error: rjrError,
  };

  return {
    altriaLoadedDataWithPagination,
    msaLoadedDataWithPagination,
    rjrLoadedDataWithPagination,
  };
};
